import { useState } from 'react';
import {
  useCreateSlot,
  useCreateSlotsBulk,
  useDeleteSlot,
  useMyAvailability,
} from '../../api/sessions';
import { Badge } from '../../components/Badge';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { expandWeekly } from '../../lib/recurrence';
import { formatSlotRange, toLocalInput } from '../../lib/format';
import { ApiError } from '../../lib/api';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Recurrence is expanded here, in the browser, and posted as explicit
 * instants — the API takes a list of exact start/end times and deliberately
 * does not grow a "repeat weekly" rule of its own. Adding seven days to a
 * timestamp server-side silently shifts the tutor's local lesson time by an
 * hour across a daylight-saving boundary; the browser knows both their
 * wall-clock intent and their zone, so it can step the local time correctly
 * and let the Date object resolve each instant.
 */
export function ScheduleTab() {
  const { notify } = useToast();
  const [includeBooked, setIncludeBooked] = useState(true);
  const slots = useMyAvailability({ includeBooked }, true);
  const createSlot = useCreateSlot();
  const createBulk = useCreateSlotsBulk();
  const deleteSlot = useDeleteSlot();

  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 25, 0, 0, 0);
    return toLocalInput(d.toISOString());
  });
  const [duration, setDuration] = useState(60);
  const [repeat, setRepeat] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [weeks, setWeeks] = useState(4);

  const preview = repeat ? expandWeekly(start, duration, weekdays, weeks) : [];

  const addSlots = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (repeat) {
        if (preview.length === 0) {
          notify('Pick at least one weekday in the future', 'error');
          return;
        }
        const result = await createBulk.mutateAsync({
          slots: preview,
          skipConflicts: true,
        });
        notify(
          `Added ${result.created.length} slots` +
            (result.skipped.length ? `, skipped ${result.skipped.length} that overlapped` : ''),
          'success',
        );
      } else {
        const startAt = new Date(start);
        await createSlot.mutateAsync({
          startTime: startAt.toISOString(),
          endTime: new Date(startAt.getTime() + duration * 60_000).toISOString(),
        });
        notify('Slot added', 'success');
      }
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not add availability.', 'error');
    }
  };

  const remove = async (slotId: string) => {
    try {
      await deleteSlot.mutateAsync(slotId);
      notify('Slot removed', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not remove this slot.', 'error');
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
      <div className="panel p-4">
        <h3 className="text-sm font-semibold text-ink">Open a time</h3>
        <p className="mt-0.5 text-xs text-ink-3">
          Times are in your browser's timezone.
        </p>

        <form onSubmit={addSlots} className="mt-4 space-y-4">
          <label className="block">
            <span className="label">Starts</span>
            <input
              type="datetime-local"
              className="input"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
            />
          </label>

          <label className="block">
            <span className="label">Length</span>
            <select
              className="select"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {[30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-[0.8125rem] text-ink">
            <input
              type="checkbox"
              checked={repeat}
              onChange={(e) => setRepeat(e.target.checked)}
            />
            Repeat weekly
          </label>

          {repeat && (
            <div className="space-y-3 rounded-md border border-rule bg-surface-2 p-3">
              <div>
                <span className="label">On these days</span>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((label, index) => {
                    const on = weekdays.includes(index);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          setWeekdays((prev) =>
                            on ? prev.filter((d) => d !== index) : [...prev, index],
                          )
                        }
                        className={`rounded px-2 py-1 text-[0.6875rem] ${
                          on
                            ? 'bg-accent text-white'
                            : 'bg-surface text-ink-2 hover:bg-surface-hover'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="label">For how many weeks</span>
                <input
                  type="number"
                  min={1}
                  max={26}
                  className="input"
                  value={weeks}
                  onChange={(e) => setWeeks(Number(e.target.value))}
                />
              </label>

              <p className="text-[0.6875rem] text-ink-3">
                {preview.length} slots will be created. Any that clash with an
                existing time are skipped.
              </p>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={createSlot.isPending || createBulk.isPending}
          >
            {createSlot.isPending || createBulk.isPending ? 'Adding…' : 'Add to their calendar'}
          </button>
        </form>
      </div>

      <div className="panel overflow-hidden">
        <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
          <h3 className="text-sm font-semibold text-ink">Their calendar</h3>
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            <input
              type="checkbox"
              checked={includeBooked}
              onChange={(e) => setIncludeBooked(e.target.checked)}
            />
            Show booked times
          </label>
        </div>

        {slots.isLoading ? (
          <div className="flex h-32 items-center justify-center text-ink-3">
            <Spinner />
          </div>
        ) : slots.isError ? (
          <ErrorState
            message={slots.error instanceof ApiError ? slots.error.message : undefined}
            onRetry={() => void slots.refetch()}
          />
        ) : slots.data && slots.data.length > 0 ? (
          <ul className="divide-y divide-rule">
            {slots.data.map((slot) => (
              <li key={slot.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="flex-1 text-[0.8125rem] text-ink">
                  {formatSlotRange(slot.startTime, slot.endTime)}
                </span>
                {slot.isBooked ? (
                  <Badge tone="accent">Booked</Badge>
                ) : (
                  <Badge tone="good">Open</Badge>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-danger"
                  disabled={slot.isBooked || deleteSlot.isPending}
                  title={
                    slot.isBooked
                      ? 'Cancel the lesson instead — that frees the slot'
                      : undefined
                  }
                  onClick={() => void remove(slot.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No open times"
            description="Nothing is published, so students cannot book this tutor. Add a time on the left."
          />
        )}
      </div>
    </div>
  );
}
